/**
 * A hook to allow access to search results data.
 *
 * @module
 */
import { useSelector, useDispatch } from '../actions/types';
import { Actions as SearchActions } from '../actions/search';
/**
 * # useSearchResults
 *
 * Usage example:
 * ```javascript
 * import useSearchResults from '@elevio/kb-kit/lib/hooks/useSearchResults';
 *
 * const MySearchResults = () => {
 *   const {
 *     clearResults,
 *     hasMore,
 *     isFetching,
 *     loadMore,
 *     results,
 *     searchTerm,
 *     totalCount,
 *   } = useSearchResults();
 *   console.log(clearResults); // Function
 *   console.log(hasMore); // boolean
 *   console.log(isFetching); // boolean
 *   console.log(loadMore); // Function
 *   console.log(results); // ArticleSearchResult[]
 *   console.log(searchTerm); // string | null
 *   console.log(totalCount); // number | null
 *
 *   if (!hasMore || !results) return null;
 *
 *   if (isFetching) return <div>Fetching...</div>;
 *
 *   return (
 *     <div>
 *       <div>
 *         Total results for "{searchTerm}": {totalCount}
 *       </div>
 *       <ul>
 *         {results.map((result) => {
 *           return <li key={result.id}>{result.title}</li>;
 *         })}
 *       </ul>
 *       {hasMore ? <button onClick={loadMore}>Fetch more...</button> : null}
 *       <button onClick={clearResults}>Clear results</button>
 *     </div>
 *   );
 * };
 * ```
 */
export default function useSearchResults() {
    const dispatch = useDispatch();
    const results = useSelector((state) => state.search.results);
    const searchTerm = useSelector((state) => state.search.searchedTerm);
    const isFetching = useSelector((state) => state.search.isFetching);
    const totalCount = useSelector((state) => state.search.totalCount);
    const currentPage = useSelector((state) => state.search.currentPage);
    const totalPages = useSelector((state) => state.search.totalPages);
    const hasMore = currentPage && totalPages ? currentPage < totalPages : false;
    const loadMore = () => {
        if (!currentPage || !searchTerm || !hasMore)
            return;
        dispatch(SearchActions.searchRequest({ query: searchTerm, page: currentPage + 1 }));
    };
    const clearResults = () => dispatch(SearchActions.searchReset());
    return {
        isFetching,
        searchTerm,
        hasMore,
        results,
        loadMore,
        totalCount,
        clearResults,
    };
}
//# sourceMappingURL=useSearchResults.js.map